import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { User, ExternalLink, Users, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import { emitToast } from '../context/toast';
import { isLiveClassClosed, isLiveClassEnrollable, isLiveClassInProgress } from '../utils/liveClassEnrollment';
import { isVirtualGymMember } from './dashboard/userServices';
import { D28D_SCHEDULE_TZ, timeFromIsoInScheduleTz, dateKeyInScheduleTz, hourInScheduleTz } from '../utils/d28dScheduleTimezone';
import {
  buildEnrollSuccessMessage,
  rememberEnrollBanner,
  resolveClassEndMs,
} from '../utils/liveClassEnrollBanner';
import LiveClassEnrollBanner from './dashboard/LiveClassEnrollBanner';
import './LiveClassSchedule.css';

const WEEKDAY_IDS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_MATCH = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
const TZ = D28D_SCHEDULE_TZ;
const MS_DAY = 24 * 60 * 60 * 1000;

function localeTag(lang) {
  return lang === 'en' ? 'en-US' : 'es-CO';
}

function normalizeDay(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .slice(0, 3);
}

function timeFromIso(iso, lang = 'es') {
  return timeFromIsoInScheduleTz(iso, lang, TZ);
}

function dateFromIso(iso) {
  return dateKeyInScheduleTz(iso, TZ);
}

function weekdayFromClass(classItem) {
  if (classItem.day_label) return normalizeDay(classItem.day_label);
  try {
    const name = new Intl.DateTimeFormat('es-ES', { weekday: 'long', timeZone: TZ }).format(new Date(classItem.start_time));
    return normalizeDay(name);
  } catch {
    return '';
  }
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekLabel(weekStart, lang = 'es') {
  const end = new Date(weekStart.getTime() + 6 * MS_DAY);
  const fmt = new Intl.DateTimeFormat(localeTag(lang), { day: 'numeric', month: 'short', timeZone: TZ });
  return `${fmt.format(weekStart)} – ${fmt.format(end)}`;
}

function buildWeekColumnDates(weekStart) {
  return WEEKDAY_MATCH.map((_, idx) => new Date(weekStart.getTime() + idx * MS_DAY));
}

function formatWeekdayHeader(date, lang = 'es') {
  return new Intl.DateTimeFormat(localeTag(lang), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: TZ,
  }).format(date);
}

function minutesInScheduleTz(iso) {
  const h = hourInScheduleTz(iso, TZ);
  try {
    const m = Number(new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ,
      minute: '2-digit',
    }).format(new Date(iso)));
    return h * 60 + (Number.isFinite(m) ? m : 0);
  } catch {
    return h * 60;
  }
}

function compareScheduleSlots(a, b) {
  if (a.sortStart !== b.sortStart) return a.sortStart - b.sortStart;
  return a.sortEnd - b.sortEnd;
}

function slotPeriod(start) {
  const h = parseInt(String(start).slice(0, 2), 10);
  if (Number.isNaN(h)) return 'morning';
  return h >= 12 ? 'evening' : 'morning';
}

function matchesSlot(classItem, slot, lang = 'es') {
  const start = timeFromIso(classItem.start_time, lang);
  const end = timeFromIso(classItem.end_time, lang);
  if (!start) return false;
  return start === slot.start && (end === slot.end || !slot.end);
}

function isUserEnrolled(classItem, userId) {
  if (!userId || !classItem) return false;
  const ids = Array.isArray(classItem.enrolled_user_ids) ? classItem.enrolled_user_ids : [];
  return ids.some((id) => Number(id) === Number(userId));
}

function spotsAvailable(classItem) {
  const total = Number(classItem.capacity) || 40;
  const enrolled = classItem.enrolled_user_ids?.length || 0;
  return Math.max(0, total - enrolled);
}

function methodLabel(classItem, defaultLabel = 'Clase en vivo') {
  return String(classItem.class_type || classItem.title || '').trim() || defaultLabel;
}

function isInWeek(iso, weekStart) {
  const t = new Date(iso).getTime();
  const start = weekStart.getTime();
  const end = start + 7 * MS_DAY;
  return t >= start && t < end;
}

function ClassCard({
  classItem,
  slot,
  userId,
  enrolling,
  onEnroll,
  onJoin,
  compact = false,
}) {
  const { t } = useI18n();
  const isEnrolled = isUserEnrolled(classItem, userId);
  const available = spotsAvailable(classItem);
  const isFull = !isEnrolled && available <= 0;
  const lastSpot = !isEnrolled && available === 1;
  const canEnroll = isLiveClassEnrollable(classItem);
  const isClosed = isLiveClassClosed(classItem);
  const inProgress = isLiveClassInProgress(classItem);
  const hasZoom = Boolean(String(classItem.zoom_link || '').trim());
  const cardClass = `${slot.period === 'evening'
    ? 'd28d-graphic-schedule__card d28d-graphic-schedule__card--evening'
    : 'd28d-graphic-schedule__card d28d-graphic-schedule__card--morning'}${compact ? ' d28d-graphic-schedule__card--compact' : ''}${isClosed ? ' d28d-graphic-schedule__card--closed' : ''}`;
  const classId = Number(classItem.id);
  const defaultClass = t('schedule.default_class', 'Clase en vivo');
  const defaultCoach = t('schedule.default_coach', 'Entrenador');

  return (
    <div className={cardClass}>
      <div className="d28d-graphic-schedule__card-kicker">
        {methodLabel(classItem, defaultClass)}
        {isClosed ? (
          <span className="d28d-graphic-schedule__closed-badge">{t('schedule.class_closed', 'Cerrada')}</span>
        ) : null}
      </div>
      <div className="d28d-graphic-schedule__card-title" title={classItem.title}>
        {classItem.title}
      </div>
      <div className="d28d-graphic-schedule__card-meta">
        <User className="w-3 h-3 shrink-0" />
        <span className="truncate">{classItem.coach || defaultCoach}</span>
      </div>
      <div className="d28d-graphic-schedule__card-meta">
        <Users className="w-3 h-3 shrink-0" />
        <span>
          {t('schedule.spots_available', '{count} disponibles', { count: available })}
          {lastSpot ? t('schedule.last_spot_suffix', ' · ¡Último cupo!') : ''}
        </span>
      </div>
      <div className="d28d-graphic-schedule__card-actions">
        {!isEnrolled ? (
          canEnroll && inProgress && hasZoom ? (
          <button
            type="button"
            className="d28d-graphic-schedule__btn-zoom"
            onClick={() => onJoin(classId, classItem.zoom_link)}
          >
            <ExternalLink className="w-3 h-3" />
            {t('home.enter_zoom', 'Entrar a Zoom')}
          </button>
          ) : canEnroll ? (
          <button
            type="button"
            className="d28d-graphic-schedule__btn-enroll"
            disabled={isFull || enrolling === classId}
            onClick={() => onEnroll(classId)}
          >
            {enrolling === classId ? '…' : isFull ? t('schedule.no_spots', 'Sin cupos') : lastSpot ? t('schedule.last_spot', '¡Último cupo!') : t('schedule.enroll', 'Inscribirme')}
          </button>
          ) : (
            <span className="text-xs d28d-text-muted">
              {isClosed
                ? t('schedule.class_closed', 'Clase cerrada')
                : t('schedule.enrollment_closed', 'Inscripción cerrada')}
            </span>
          )
        ) : isClosed ? (
          <span className="text-xs d28d-text-muted">{t('schedule.class_closed', 'Clase cerrada')}</span>
        ) : (
          hasZoom ? (
          <button
            type="button"
            className="d28d-graphic-schedule__btn-zoom"
            onClick={() => onJoin(classId, classItem.zoom_link)}
          >
            <ExternalLink className="w-3 h-3" />
            {t('home.enter_zoom', 'Entrar a Zoom')}
          </button>
          ) : (
            <span className="text-xs text-amber-700 leading-snug">
              {t('schedule.zoom_pending', 'Enlace pendiente: tu entrenador lo publicará antes de la clase.')}
            </span>
          )
        )}
      </div>
    </div>
  );
}

const SCHEDULE_LS = 'd28d.schedule.filters';
const classesCache = { userId: '', key: '', data: [], ts: 0 };
const CLASSES_CACHE_MS = 90000;

function scheduleCacheKey(userId, programId, scopeAll, sedeMode) {
  return `${userId || 'anon'}|${sedeMode ? 'sede' : (programId || 'all')}|${scopeAll}`;
}

export function invalidateLiveClassesCache() {
  classesCache.userId = '';
  classesCache.key = '';
  classesCache.data = [];
  classesCache.ts = 0;
}

function readScheduleFilters() {
  try {
    const raw = sessionStorage.getItem(SCHEDULE_LS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function LiveClassSchedule({
  programId,
  globalMode = false,
  sedeMode = false,
  previewMode = false,
  canCloneWeek = false,
  scopeGymId = null,
}) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const virtualGymMember = isVirtualGymMember(user);
  const defaultClassLabel = t('schedule.default_class', 'Clase en vivo');
  const labelMethod = (classItem) => methodLabel(classItem, defaultClassLabel);
  const savedFilters = useMemo(() => readScheduleFilters(), []);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [enrolling, setEnrolling] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [methodQuery, setMethodQuery] = useState(savedFilters.methodQuery || '');
  const [selectedDay, setSelectedDay] = useState(savedFilters.selectedDay || '');
  const [selectedDate, setSelectedDate] = useState(savedFilters.selectedDate || '');
  const [coachFilter, setCoachFilter] = useState(savedFilters.coachFilter || '');
  const [classTypeFilter, setClassTypeFilter] = useState(
    savedFilters.classTypeFilter || savedFilters.categoryFilter || '',
  );
  const [scopeAll, setScopeAll] = useState(globalMode && !virtualGymMember);
  const [cloningWeek, setCloningWeek] = useState(false);

  useEffect(() => {
    if (virtualGymMember && scopeAll) setScopeAll(false);
  }, [virtualGymMember, scopeAll]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SCHEDULE_LS, JSON.stringify({
        methodQuery,
        selectedDay,
        selectedDate,
        coachFilter,
        classTypeFilter,
      }));
    } catch { /* ignore */ }
  }, [methodQuery, selectedDay, selectedDate, coachFilter, classTypeFilter]);

  const fetchClasses = async (force = false) => {
    const userId = user?.id != null ? String(user.id) : 'anon';
    const cacheKey = scheduleCacheKey(userId, programId, scopeAll, sedeMode);
    if (!force && classesCache.key === cacheKey && classesCache.userId === userId && Date.now() - classesCache.ts < CLASSES_CACHE_MS) {
      setClasses(classesCache.data);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setNotice('');
      const params = {};
      if (!sedeMode && programId) params.program_id = programId;
      const res = await api.get('/live-classes', { params });
      const next = (res.data.data || []).filter((c) => c.active !== false);
      classesCache.userId = userId;
      classesCache.key = cacheKey;
      classesCache.data = next;
      classesCache.ts = Date.now();
      setClasses(next);
    } catch {
      setNotice('No pudimos cargar el horario. Intenta de nuevo en unos segundos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    invalidateLiveClassesCache();
    fetchClasses(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, scopeAll, sedeMode, user?.id]);

  const coachOptions = useMemo(() => {
    const set = new Set();
    classes.forEach((c) => {
      const coach = String(c.coach || '').trim();
      if (coach) set.add(coach);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [classes]);

  const classTypeOptions = useMemo(() => {
    const set = new Set();
    classes.forEach((c) => {
      const type = String(c.class_type || c.title || '').trim();
      if (type) set.add(type);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [classes]);

  const weekDates = useMemo(() => buildWeekColumnDates(weekStart), [weekStart]);

  const filteredClasses = useMemo(() => {
    const q = methodQuery.trim().toLowerCase();
    return classes.filter((c) => {
      if (!isInWeek(c.start_time, weekStart)) return false;
      if (selectedDate && dateFromIso(c.start_time) !== selectedDate) return false;
      if (selectedDay && weekdayFromClass(c) !== normalizeDay(
        WEEKDAY_MATCH[WEEKDAY_IDS.indexOf(selectedDay)] || selectedDay,
      )) return false;
      if (coachFilter && String(c.coach || '').trim() !== coachFilter) return false;
      if (classTypeFilter) {
        const type = String(c.class_type || c.title || '').trim();
        if (type !== classTypeFilter) return false;
      }
      if (q) {
        const hay = `${c.title} ${c.class_type} ${c.coach} ${labelMethod(c)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [classes, weekStart, selectedDay, selectedDate, methodQuery, coachFilter, classTypeFilter]);

  const timeSlots = useMemo(() => {
    const seen = new Map();
    filteredClasses.forEach((c) => {
      const start = timeFromIso(c.start_time, lang);
      const end = timeFromIso(c.end_time, lang);
      if (!start) return;
      const sortStart = minutesInScheduleTz(c.start_time);
      const sortEnd = minutesInScheduleTz(c.end_time);
      const key = `${sortStart}|${sortEnd}`;
      if (!seen.has(key)) {
        const period = slotPeriod(start);
        const suffix = period === 'evening'
          ? ` ${t('schedule.pm', 'P.M.')}`
          : ` ${t('schedule.am', 'A.M.')}`;
        seen.set(key, {
          start,
          end,
          period,
          suffix,
          label: `${start}${end ? `-${end}` : ''}${suffix}`,
          sortStart,
          sortEnd,
        });
      }
    });
    return [...seen.values()].sort(compareScheduleSlots);
  }, [filteredClasses, lang, t]);

  const getClassesForSlot = (dayName, slot) => {
    const dayKey = normalizeDay(dayName);
    return filteredClasses
      .filter((c) => weekdayFromClass(c) === dayKey && matchesSlot(c, slot, lang))
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  };

  const applyEnrolledPatch = (list, classId, enrolledIds) => (
    (list || []).map((c) => (
      Number(c.id) === classId
        ? { ...c, enrolled_user_ids: Array.isArray(enrolledIds) ? enrolledIds : c.enrolled_user_ids }
        : c
    ))
  );

  const handleEnroll = async (classId) => {
    const id = Number(classId);
    if (!Number.isFinite(id)) return;
    const classItem = classes.find((c) => Number(c.id) === id);
    if (!classItem || !isLiveClassEnrollable(classItem)) {
      emitToast({
        type: 'warning',
        title: t('schedule.enroll_fail', 'Inscripción'),
        message: t('schedule.enrollment_closed', 'Esta clase ya terminó. No hay inscripciones abiertas.'),
      });
      return;
    }
    if (isUserEnrolled(classItem, user?.id)) return;
    try {
      setEnrolling(id);
      const res = await api.post(`/live-classes/${id}/enroll`);
      const updated = res.data?.data;
      if (updated?.enrolled_user_ids) {
        const patch = (list) => applyEnrolledPatch(list, id, updated.enrolled_user_ids);
        setClasses(patch);
        classesCache.data = patch(classesCache.data);
        classesCache.ts = Date.now();
      }
      await fetchClasses(true);
      rememberEnrollBanner({
        userId: user?.id,
        classId: id,
        classTitle: updated?.title || classItem.title,
        endMs: resolveClassEndMs(updated || classItem),
      });
      emitToast({
        type: 'success',
        title: t('schedule.enroll_ok_title', 'Inscripción confirmada'),
        message: buildEnrollSuccessMessage(updated?.title || classItem.title),
        duration: 12000,
      });
      try {
        window.dispatchEvent(new CustomEvent('d28d:progress:refresh'));
        window.dispatchEvent(new CustomEvent('d28d:notifications:refresh'));
      } catch { /* noop */ }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || t('schedule.enroll_error', 'No se pudo completar la inscripción');
      if (status === 404 || status === 403) {
        invalidateLiveClassesCache();
        await fetchClasses(true);
      }
      emitToast({ type: 'error', title: t('schedule.enroll_fail', 'Inscripción'), message: msg });
    } finally {
      setEnrolling(null);
    }
  };

  const handleJoin = async (classId, zoomLink) => {
    const id = Number(classId);
    const link = String(zoomLink || '').trim();
    if (!link) {
      emitToast({
        type: 'info',
        title: t('schedule.no_zoom', 'Sin enlace Zoom'),
        message: t(
          'schedule.no_zoom_trainer_hint',
          'Tu entrenador aún no ha publicado el enlace. Revisa de nuevo más cerca de la hora de la clase.',
        ),
      });
      return;
    }
    try {
      await api.post(`/live-classes/${id}/join`);
      try {
        window.dispatchEvent(new CustomEvent('d28d:progress:refresh'));
      } catch { /* noop */ }
    } catch (err) {
      const msg = err.response?.data?.error;
      if (msg) {
        emitToast({ type: 'warning', title: t('schedule.attendance', 'Asistencia'), message: msg });
      }
    }
    const opened = window.open(link, '_blank', 'noopener,noreferrer');
    if (!opened) {
      window.location.assign(link);
    }
  };

  const shiftWeek = (delta) => {
    setWeekStart((prev) => new Date(prev.getTime() + delta * 7 * MS_DAY));
    setSelectedDate('');
  };

  const handleCloneWeek = async () => {
    if (!canCloneWeek || cloningWeek) return;
    const monday = startOfWeek(weekStart);
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, '0');
    const dd = String(monday.getDate()).padStart(2, '0');
    const label = formatWeekLabel(monday, lang);
    if (!window.confirm(
      t(
        'schedule.clone_week_confirm',
        '¿Clonar toda la programación de {week} a la semana siguiente (mismas clases, +7 días)?',
        { week: label },
      ),
    )) return;
    setCloningWeek(true);
    try {
      const res = await api.post('/live-classes/admin/clone-week', {
        source_week_start: `${yyyy}-${mm}-${dd}`,
        ...(scopeGymId != null && scopeGymId !== '' ? { gym_id: Number(scopeGymId) } : {}),
        ...(programId ? { program_id: programId } : {}),
      });
      const data = res.data?.data || {};
      emitToast({
        type: 'success',
        title: t('schedule.clone_week_ok_title', 'Semana clonada'),
        message: t(
          'schedule.clone_week_ok',
          'Se crearon {n} clases en la semana siguiente ({skipped} omitidas por duplicado).',
          { n: data.created_count || 0, skipped: data.skipped_count || 0 },
        ),
      });
      await fetchClasses(true);
      shiftWeek(1);
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: err?.response?.data?.error || t('schedule.clone_week_error', 'No se pudo clonar la semana.'),
      });
    } finally {
      setCloningWeek(false);
    }
  };

  const onPickDate = (value) => {
    setSelectedDate(value);
    if (value) {
      setWeekStart(startOfWeek(new Date(`${value}T12:00:00`)));
    }
  };

  if (loading) {
    return (
      <div className="d28d-graphic-schedule surface-readable flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500" />
      </div>
    );
  }

  if (previewMode) {
    const preview = [...filteredClasses]
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .slice(0, 4);
    if (!preview.length) {
      return (
        <div className="card p-4 text-sm d28d-text-muted">
          {t('schedule.empty_week', 'No hay clases para esta semana con los filtros seleccionados.')}
        </div>
      );
    }
    return (
      <div className="d28d-graphic-schedule__list card p-4">
        <ul className="d28d-graphic-schedule__list-items">
          {preview.map((classItem) => {
            const isEnrolled = isUserEnrolled(classItem, user?.id);
            const classId = Number(classItem.id);
            const dayLabel = classItem.day_label
              || new Intl.DateTimeFormat(localeTag(lang), { weekday: 'short', timeZone: TZ }).format(new Date(classItem.start_time));
            return (
              <li key={classItem.id} className="d28d-graphic-schedule__list-row">
                <div>
                  <div className="font-semibold text-sm">{classItem.title}</div>
                  <div className="text-xs opacity-80">
                    {dayLabel} · {timeFromIso(classItem.start_time, lang)} · {labelMethod(classItem)}
                  </div>
                </div>
                {!isEnrolled ? (
                  isLiveClassEnrollable(classItem)
                    && isLiveClassInProgress(classItem)
                    && String(classItem.zoom_link || '').trim() ? (
                  <button
                    type="button"
                    className="d28d-graphic-schedule__btn-zoom"
                    style={{ width: 'auto', minWidth: '7rem' }}
                    onClick={() => handleJoin(classId, classItem.zoom_link)}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('home.enter_zoom', 'Entrar a Zoom')}
                  </button>
                  ) : isLiveClassEnrollable(classItem) ? (
                  <button
                    type="button"
                    className="d28d-graphic-schedule__btn-enroll"
                    style={{ width: 'auto', minWidth: '7rem' }}
                    disabled={enrolling === classId}
                    onClick={() => handleEnroll(classId)}
                  >
                    {enrolling === classId ? '…' : t('schedule.enroll', 'Inscribirme')}
                  </button>
                  ) : (
                    <span className="text-xs opacity-70">{t('schedule.class_closed', 'Clase cerrada')}</span>
                  )
                ) : isLiveClassClosed(classItem) ? (
                  <span className="text-xs opacity-70">{t('schedule.class_closed', 'Clase cerrada')}</span>
                ) : String(classItem.zoom_link || '').trim() ? (
                  <button
                    type="button"
                    className="d28d-graphic-schedule__btn-zoom"
                    style={{ width: 'auto', minWidth: '7rem' }}
                    onClick={() => handleJoin(classId, classItem.zoom_link)}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('home.enter_zoom', 'Entrar a Zoom')}
                  </button>
                  ) : (
                    <span className="text-xs text-amber-700 max-w-[10rem] leading-snug">
                      {t('schedule.zoom_pending', 'Enlace pendiente del entrenador')}
                    </span>
                  )
                }
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="d28d-graphic-schedule surface-readable space-y-4">
      <LiveClassEnrollBanner />
      <div className="d28d-graphic-schedule__toolbar">
        <div className="d28d-graphic-schedule__search">
          <Search className="w-4 h-4 shrink-0 opacity-70" />
          <input
            type="search"
            value={methodQuery}
            onChange={(e) => setMethodQuery(e.target.value)}
            placeholder={t('schedule.search_ph', 'Buscar por método, nombre o entrenador…')}
            aria-label={t('schedule.search', 'Buscar clases')}
          />
        </div>
        <select
          className="d28d-graphic-schedule__select"
          value={coachFilter}
          onChange={(e) => setCoachFilter(e.target.value)}
          aria-label={t('schedule.filter_coach', 'Filtrar por entrenador')}
        >
          <option value="">{t('schedule.all_coaches', 'Todos los entrenadores')}</option>
          {coachOptions.map((coach) => (
            <option key={coach} value={coach}>{coach}</option>
          ))}
        </select>
        <select
          className="d28d-graphic-schedule__select"
          value={classTypeFilter}
          onChange={(e) => setClassTypeFilter(e.target.value)}
          aria-label={t('schedule.filter_class_type', 'Filtrar por tipo de clase')}
        >
          <option value="">{t('schedule.all_class_types', 'Todos los tipos de clase')}</option>
          {classTypeOptions.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <select
          className="d28d-graphic-schedule__select"
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
          aria-label={t('schedule.filter_day', 'Filtrar por día')}
        >
          <option value="">{t('schedule.all_days', 'Todos los días')}</option>
          {WEEKDAY_IDS.map((id) => (
            <option key={id} value={id}>{t(`schedule.weekday.${id}`, id)}</option>
          ))}
        </select>
        <input
          type="date"
          className="d28d-graphic-schedule__date"
          value={selectedDate}
          onChange={(e) => onPickDate(e.target.value)}
          aria-label={t('schedule.filter_date', 'Filtrar por fecha')}
        />
        <div className="d28d-graphic-schedule__week-nav">
          <button type="button" onClick={() => shiftWeek(-1)} aria-label={t('schedule.prev_week', 'Semana anterior')}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span>{formatWeekLabel(weekStart, lang)}</span>
          <button type="button" onClick={() => shiftWeek(1)} aria-label={t('schedule.next_week', 'Semana siguiente')}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {canCloneWeek ? (
          <button
            type="button"
            className="btn-secondary text-sm whitespace-nowrap"
            disabled={cloningWeek}
            onClick={handleCloneWeek}
            title={t('schedule.clone_week_hint', 'Copia todas las clases de esta semana a la siguiente (+7 días)')}
          >
            {cloningWeek
              ? t('schedule.clone_week_busy', 'Clonando…')
              : t('schedule.clone_week', 'Clonar semana → siguiente')}
          </button>
        ) : null}
      </div>

      <p className="text-xs text-stone-600 px-1">
        Los días festivos en Colombia tendrán una programación diferente.
        {' '}
        <span className="d28d-graphic-schedule__scroll-hint">
          Semana lun–dom: desliza horizontalmente si no ves viernes, sábado o domingo.
        </span>
      </p>

      {notice ? <p className="text-sm text-red-600">{notice}</p> : null}

      {filteredClasses.length === 0 ? (
        <div className="d28d-graphic-schedule__empty-state">
          <p>{t('schedule.empty_week', 'No hay clases para esta semana con los filtros seleccionados.')}</p>
          <button
            type="button"
            className="d28d-graphic-schedule__btn-enroll"
            style={{ maxWidth: '14rem' }}
            onClick={() => {
              setMethodQuery('');
              setSelectedDay('');
              setSelectedDate('');
              setWeekStart(startOfWeek(new Date()));
            }}
          >
            {t('schedule.reset_week', 'Ver semana actual')}
          </button>
        </div>
      ) : (
        <div className="d28d-graphic-schedule__table-wrap">
          <table className="d28d-graphic-schedule__table">
            <thead>
              <tr>
                <th>{t('schedule.time_day', 'Hora / Día')}</th>
                {WEEKDAY_IDS.map((id, idx) => (
                  <th key={id}>{formatWeekdayHeader(weekDates[idx], lang)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((slot) => (
                <tr key={`${slot.sortStart}-${slot.sortEnd}`}>
                  <td className="d28d-graphic-schedule__time-cell">
                    <span className="d28d-graphic-schedule__time-start">{slot.start}</span>
                    <span className="d28d-graphic-schedule__time-sep">–</span>
                    <span className="d28d-graphic-schedule__time-end">{slot.end}</span>
                    <span className="d28d-graphic-schedule__time-ampm">{slot.suffix}</span>
                  </td>
                  {WEEKDAY_MATCH.map((day, idx) => {
                    const slotClasses = getClassesForSlot(day, slot);
                    if (!slotClasses.length) {
                      return <td key={WEEKDAY_IDS[idx]} className="d28d-graphic-schedule__empty-cell" />;
                    }
                    return (
                      <td key={WEEKDAY_IDS[idx]} className="d28d-graphic-schedule__data-cell">
                        <div className="d28d-graphic-schedule__cell-stack">
                          {slotClasses.map((classItem) => (
                            <ClassCard
                              key={classItem.id}
                              classItem={classItem}
                              slot={slot}
                              userId={user?.id}
                              enrolling={enrolling}
                              onEnroll={handleEnroll}
                              onJoin={handleJoin}
                              compact={false}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredClasses.length > 0 ? (
        <div className="d28d-graphic-schedule__list">
          <h3 className="d28d-graphic-schedule__list-title">{t('schedule.week_sessions', 'Sesiones de la semana')}</h3>
          <ul className="d28d-graphic-schedule__list-items">
            {[...filteredClasses]
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
              .map((classItem) => {
                const isEnrolled = isUserEnrolled(classItem, user?.id);
                const available = spotsAvailable(classItem);
                const isFull = !isEnrolled && available <= 0;
                const classId = Number(classItem.id);
                const dayLabel = classItem.day_label
                  || new Intl.DateTimeFormat(localeTag(lang), { weekday: 'long', timeZone: TZ }).format(new Date(classItem.start_time));
                return (
                  <li key={classItem.id} className="d28d-graphic-schedule__list-row">
                    <div>
                      <div className="font-semibold text-sm">{classItem.title}</div>
                      <div className="text-xs opacity-80">
                        {dayLabel} · {timeFromIso(classItem.start_time, lang)} · {labelMethod(classItem)}
                        {' · '}{available} {t('schedule.spots', 'cupos')}
                      </div>
                    </div>
                    {!isEnrolled ? (
                      isLiveClassEnrollable(classItem)
                        && isLiveClassInProgress(classItem)
                        && String(classItem.zoom_link || '').trim() ? (
                      <button
                        type="button"
                        className="d28d-graphic-schedule__btn-zoom"
                        style={{ width: 'auto', minWidth: '7rem' }}
                        onClick={() => handleJoin(classId, classItem.zoom_link)}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {t('home.enter_zoom', 'Entrar a Zoom')}
                      </button>
                      ) : isLiveClassEnrollable(classItem) ? (
                      <button
                        type="button"
                        className="d28d-graphic-schedule__btn-enroll"
                        style={{ width: 'auto', minWidth: '7rem' }}
                        disabled={isFull || enrolling === classId}
                        onClick={() => handleEnroll(classId)}
                      >
                        {enrolling === classId ? '…' : isFull ? t('schedule.no_spots', 'Sin cupos') : t('schedule.enroll', 'Inscribirme')}
                      </button>
                      ) : (
                        <span className="text-xs opacity-70">{t('schedule.class_closed', 'Clase cerrada')}</span>
                      )
                    ) : isLiveClassClosed(classItem) ? (
                      <span className="text-xs opacity-70">{t('schedule.class_closed', 'Clase cerrada')}</span>
                    ) : String(classItem.zoom_link || '').trim() ? (
                      <button
                        type="button"
                        className="d28d-graphic-schedule__btn-zoom"
                        style={{ width: 'auto', minWidth: '7rem' }}
                        onClick={() => handleJoin(classId, classItem.zoom_link)}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {t('home.enter_zoom', 'Entrar a Zoom')}
                      </button>
                      ) : (
                        <span className="text-xs text-amber-700 max-w-[10rem] leading-snug">
                          {t('schedule.zoom_pending', 'Enlace pendiente del entrenador')}
                        </span>
                      )
                    }
                  </li>
                );
              })}
          </ul>
        </div>
      ) : null}

      <div className="d28d-graphic-schedule__brand-footer" aria-hidden>
        <div>{t('schedule.brand_line1', 'FIT LIVE')}</div>
        <div>{t('schedule.brand_line2', 'PROGRAMA')}</div>
      </div>
    </div>
  );
}
